/**
 * FP3-029B6C — Per-row assistant assignment control on the
 * supervising primary's caseload page.
 *
 * Renders only when the signed-in user is a primary provider with
 * at least one active assistant. Otherwise returns null so non-
 * supervising providers and assistants don't see noise.
 *
 * Wires to the existing FP3-029B6A `POST /students/{id}/route`
 * endpoint, which already enforces:
 *   - Caller must be admin family OR supervising primary OR an
 *     active assistant of that primary.
 *   - Destination must be in the same supervision group + same
 *     org + have school access to the student's school.
 *   - Routing keeps the supervising primary attached so Sarah
 *     remains the owner.
 *   - Routing appends an audit entry to `students.routing_history[]`.
 *
 * The UI uses provider-friendly language only: "Assign assistant",
 * "Reassign", "Remove" — never the word "route".
 */
import { useEffect, useRef, useState } from "react";
import { UsersThree, Plus, X, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

// Cache a single fetch of /auth/me/assistants per page render so we
// don't fire 30+ identical requests (one per student card).
// FP3-029C-2 — Keyed by user.id so the cache is invalidated on
// login swap (Devon → Sarah). Previously the module-level cache
// could leak the wrong user's assistants into the picker.
let _myAssistantsCache = null;
let _myAssistantsPromise = null;
let _myAssistantsCacheUserId = null;

function loadMyAssistants(userId) {
  if (
    _myAssistantsCache !== null &&
    _myAssistantsCacheUserId === userId
  ) {
    return Promise.resolve(_myAssistantsCache);
  }
  // Different user → reset.
  if (_myAssistantsCacheUserId !== userId) {
    _myAssistantsCache = null;
    _myAssistantsPromise = null;
    _myAssistantsCacheUserId = userId;
  }
  if (!_myAssistantsPromise) {
    _myAssistantsPromise = api
      .getMyAssistants()
      .then((r) => {
        _myAssistantsCache = r.data || null;
        return _myAssistantsCache;
      })
      .catch(() => {
        _myAssistantsCache = null;
        return null;
      });
  }
  return _myAssistantsPromise;
}

export function clearMyAssistantsCache() {
  _myAssistantsCache = null;
  _myAssistantsPromise = null;
  _myAssistantsCacheUserId = null;
}

// FP3-101 — Account-status badge copy for pre-activation assistants.
export function assistantAccountBadge(a) {
  const st = (a?.account_status || "").toLowerCase();
  if (st === "rostered") {
    return { label: "Rostered", title: "Rostered — invitation not sent" };
  }
  if (st === "invited") {
    return { label: "Invited", title: "Invited — activation pending" };
  }
  return null;
}

export default function AssistantAssignmentChip({ student, onChange, variant = "card" }) {
  const { user, provider: myProvider } = useAuth();
  const [meData, setMeData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (user?.permission_role !== "provider") return;
    let cancelled = false;
    loadMyAssistants(user?.id).then((d) => {
      if (cancelled) return;
      setMeData(d);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.permission_role, user?.id]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Bail out if not applicable. We render nothing rather than a "—"
  // so non-supervising providers see a clean caseload.
  if (user?.permission_role !== "provider") return null;
  if (!meData || meData.provider_role_type !== "primary") return null;

  // FP3-101 — Rostered/Invited (pre-activation) assistants are
  // assignable too; only ended supervision hides an assistant.
  const assignableAssistants = (meData.assistants || []).filter((a) =>
    a.assignable !== undefined
      ? a.assignable
      : a.supervision_status === "active"
  );
  if (assignableAssistants.length === 0) return null;

  // Resolve the currently-assigned assistant by intersecting
  // `student.assigned_providers` with our assignable assistants. When a
  // student carries multiple, we surface the first match (the
  // routing endpoint guarantees only one assistant per supervision
  // group at a time).
  const studentProviderIds = student.assigned_providers || [];
  const currentAssistant = assignableAssistants.find((a) =>
    studentProviderIds.includes(a.provider_id)
  );

  const apply = async (assistantProviderId) => {
    if (!myProvider?.id) return;
    setSaving(true);
    try {
      // POST /students/{id}/route — backend already enforces every
      // gate we need. When `assistantProviderId` is the primary's
      // own id, the routing endpoint just removes any other
      // assistant from the supervision group → effective "Remove".
      // FP3-101 — the caller (current supervising provider) is
      // explicitly written as the student-level supervisor so
      // shared assistants under multiple supervisors resolve
      // deterministically.
      await api.routeStudentToProvider(student.id, {
        to_provider_id: assistantProviderId || myProvider.id,
        ...(assistantProviderId
          ? { supervising_provider_id: myProvider.id }
          : {}),
      });
      const target = assistantProviderId
        ? assignableAssistants.find((a) => a.provider_id === assistantProviderId)
        : null;
      if (target) {
        toast.success(`${student.first_name} assigned to ${target.name}`);
      } else {
        toast.success(`${student.first_name} returned to me`);
      }
      // Invalidate the cached counts on the AssistantsCard so the
      // sidebar count updates after this assignment.
      _myAssistantsCache = null;
      _myAssistantsPromise = null;
      setOpen(false);
      onChange?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === "object" && detail.message) ||
        (typeof detail === "string" && detail) ||
        "Could not update assistant assignment";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Stop click propagation so opening the picker doesn't navigate
  // to the student detail page.
  const stop = (e) => e.stopPropagation();
  const currentBadge = assistantAccountBadge(currentAssistant);

  return (
    <div
      ref={wrapRef}
      className={`relative inline-flex ${variant === "row" ? "" : "self-start"}`}
      onClick={stop}
      data-testid={`student-${student.id}-assistant-chip`}
    >
      {currentAssistant ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          disabled={saving}
          data-testid={`student-${student.id}-assistant-current`}
          className={`inline-flex items-center gap-1 text-[10.5px] font-medium px-2 py-0.5 rounded-full border transition-colors ${
            variant === "row"
              ? "border-[#3F4756]/20 bg-[#EEF1F6] text-[#3F4756] hover:bg-[#E0E5EE]"
              : "border-[#3F4756]/20 bg-[#EEF1F6] text-[#3F4756] hover:bg-[#E0E5EE]"
          }`}
          title={`Assigned to ${currentAssistant.name}${currentAssistant.assistant_type ? ` · ${currentAssistant.assistant_type}` : ""}${currentBadge ? ` · ${currentBadge.title}` : ""}`}
        >
          <UsersThree size={10} weight="duotone" />
          <span className="truncate max-w-[150px]">
            {currentAssistant.first_name || currentAssistant.name}
            {currentAssistant.assistant_type && (
              <span className="opacity-70 ml-1">
                · {currentAssistant.assistant_type}
              </span>
            )}
          </span>
          {currentBadge && (
            <span
              data-testid={`student-${student.id}-assistant-current-status`}
              className="text-[8.5px] uppercase tracking-[0.06em] font-semibold px-1 py-0 rounded-full bg-[#FDF0DC] text-[#8A6B33] border border-[#E8D5A8]"
            >
              {currentBadge.label}
            </span>
          )}
          <CaretDown size={9} className="opacity-60" />
        </button>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((o) => !o);
          }}
          disabled={saving}
          data-testid={`student-${student.id}-assistant-assign`}
          className="inline-flex items-center gap-1 text-[10.5px] font-medium text-[#1E3F33] bg-[#E8F0EB] hover:bg-[#d5e5d9] px-2 py-0.5 rounded-full transition-colors"
        >
          <Plus size={10} weight="bold" />
          Assign assistant
        </button>
      )}

      {open && (
        <div
          data-testid={`student-${student.id}-assistant-menu`}
          onClick={stop}
          className="absolute z-30 right-0 mt-1 top-full min-w-[200px] bg-white border border-[#E5E3DB] rounded-lg shadow-lg p-1.5"
        >
          <p className="text-[9.5px] uppercase tracking-[0.08em] text-[#6B7280] font-semibold px-2 py-1">
            Assign to
          </p>
          {assignableAssistants.map((a) => {
            const isCurrent = currentAssistant?.provider_id === a.provider_id;
            const badge = assistantAccountBadge(a);
            return (
              <button
                key={a.provider_id}
                type="button"
                disabled={saving || isCurrent}
                data-testid={`student-${student.id}-assistant-option-${a.provider_id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isCurrent) apply(a.provider_id);
                }}
                className={`w-full text-left flex items-center justify-between gap-2 text-[12px] px-2 py-1.5 rounded ${
                  isCurrent
                    ? "bg-[#E8F0EB] text-[#1E3F33] cursor-default"
                    : "text-[#1F2937] hover:bg-[#F1F2F4]"
                }`}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  <UsersThree
                    size={11}
                    weight="duotone"
                    className="shrink-0 text-[#3F4756]"
                  />
                  <span className="truncate">{a.name}</span>
                  {a.assistant_type && (
                    <span className="text-[9.5px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded-full bg-[#EEF1F6] text-[#3F4756]">
                      {a.assistant_type}
                    </span>
                  )}
                  {badge && (
                    <span
                      data-testid={`student-${student.id}-assistant-option-${a.provider_id}-status`}
                      title={badge.title}
                      className="text-[9.5px] uppercase tracking-[0.08em] font-semibold px-1.5 py-0.5 rounded-full bg-[#FDF0DC] text-[#8A6B33] border border-[#E8D5A8]"
                    >
                      {badge.label}
                    </span>
                  )}
                </span>
                {isCurrent && (
                  <span className="text-[10px] text-[#1E3F33]">✓</span>
                )}
              </button>
            );
          })}
          {currentAssistant && (
            <>
              <div className="my-1 border-t border-[#F1F2F4]" />
              <button
                type="button"
                disabled={saving}
                data-testid={`student-${student.id}-assistant-remove`}
                onClick={(e) => {
                  e.stopPropagation();
                  apply(""); // Empty = return to primary (self).
                }}
                className="w-full text-left flex items-center gap-1.5 text-[12px] px-2 py-1.5 rounded text-[#7A3A2C] hover:bg-[#F8EFEA]"
              >
                <X size={11} />
                Remove assistant
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
